import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExternalLinkAlt,
  faArrowRight,
  faEnvelope,
  faPhone,
  faClock,
  faRocket,
  faShieldAlt,
  faUsers,
  faHeadset
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Layanan.css';

const Layanan = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  const layananList = [
    {
      id: 1,
      title: "SIAKAD",
      subtitle: "Sistem Informasi Akademik",
      description: "Portal akademik untuk mahasiswa dan dosen. Akses KRS, KHS, jadwal kuliah, dan informasi akademik lainnya.",
      icon: null,
      customIcon: "/images/siakad.png",
      url: "https://v2.stikesbaramuli.ac.id",
      color: "#1e3a8a",
      gradient: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
      shadowColor: "rgba(30, 58, 138, 0.4)",
      features: ["KRS Online", "KHS Terintegrasi", "Jadwal Kuliah"],
      available: true
    },
    {
      id: 2,
      title: "LMS",
      subtitle: "Learning Management System",
      description: "Platform pembelajaran online untuk mengakses materi kuliah, tugas, quiz, dan diskusi kelas secara digital.",
      icon: null,
      customIcon: "/images/logo.png",
      url: "https://lms2.stikesbaramuli.ac.id",
      color: "#7c3aed",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
      shadowColor: "rgba(124, 58, 237, 0.4)",
      features: ["Video Pembelajaran", "Quiz Interaktif", "Forum Diskusi"],
      available: true
    },
    {
      id: 3,
      title: "ELIB",
      subtitle: "Perpustakaan Digital",
      description: "Akses ribuan koleksi buku, jurnal, dan referensi ilmiah secara online. Tersedia 24/7 untuk mendukung kegiatan belajar Anda.",
      icon: null,
      customIcon: "/images/logo.png",
      url: "https://elib.stikesbaramuli.ac.id",
      color: "#059669",
      gradient: "linear-gradient(135deg, #059669 0%, #34d399 100%)",
      shadowColor: "rgba(5, 150, 105, 0.4)",
      features: ["E-Book", "Jurnal Ilmiah", "Akses 24/7"],
      available: true
    },
    {
      id: 4,
      title: "SIBALI",
      subtitle: "Sistem Bantuan & Layanan Informasi",
      description: "Butuh bantuan? Tim kami siap membantu menyelesaikan kendala teknis dan pertanyaan seputar layanan akademik.",
      icon: null,
      customIcon: "/images/SIBALI.png",
      url: "https://sibali.stikesbaramuli.ac.id",
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
      shadowColor: "rgba(245, 158, 11, 0.4)",
      features: ["Helpdesk", "Tiket Support", "FAQ"],
      available: true
    },
    {
      id: 5,
      title: "OJS",
      subtitle: "Open Journal System",
      description: "Portal jurnal ilmiah STIKES Baramuli. Akses publikasi penelitian, artikel ilmiah, dan submit karya tulis Anda.",
      icon: null,
      customIcon: "/images/logo.png",
      url: "https://jurnal.stikesbaramuli.ac.id",
      color: "#dc2626",
      gradient: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)",
      shadowColor: "rgba(220, 38, 38, 0.4)",
      features: ["Publikasi Jurnal", "Submit Artikel", "Review Online"],
      available: true
    },
    {
      id: 6,
      title: "RPL",
      subtitle: "Rekognisi Pembelajaran Lampau",
      description: "Layanan pengajuan dan administrasi Rekognisi Pembelajaran Lampau untuk pengakuan kompetensi dan pengalaman kerja.",
      icon: null,
      customIcon: "/images/logo.png",
      url: "https://adminrpl.stikesbaramuli.ac.id",
      color: "#0891b2",
      gradient: "linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)",
      shadowColor: "rgba(8, 145, 178, 0.4)",
      features: ["Pengajuan RPL", "Tracking Status", "Dokumen Online"],
      available: true
    }
  ];

  const stats = [
    { icon: faUsers, value: "5000+", label: "Pengguna Aktif" },
    { icon: faRocket, value: "6", label: "Layanan Digital" },
    { icon: faShieldAlt, value: "99.9%", label: "Uptime Server" },
    { icon: faHeadset, value: "24/7", label: "Dukungan IT" }
  ];

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    }, observerOptions);

    // Observe all sections
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const setRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <main className="layanan-page-modern">
      {/* Hero Section - Modern */}
      <section className="layanan-hero-modern">
        <div className="hero-bg-animation">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
          <div className="floating-shape shape-4"></div>
        </div>
        <div className="hero-overlay-gradient"></div>
        <div className="container">
          <div className="hero-content">
            <span className="hero-badge">
              <FontAwesomeIcon icon={faRocket} />
              Digital Campus
            </span>
            <h1>Layanan Digital</h1>
            <p>Akses berbagai layanan digital STIKES Baramuli untuk mendukung kegiatan akademik Anda dengan teknologi terkini</p>
            <div className="hero-scroll-indicator">
              <div className="mouse">
                <div className="wheel"></div>
              </div>
              <span>Scroll untuk melihat</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        className={`stats-section ${isVisible['stats'] ? 'visible' : ''}`}
        id="stats"
        ref={setRef('stats')}
      >
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="stat-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-icon">
                  <FontAwesomeIcon icon={stat.icon} />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Layanan Cards Section - Modern */}
      <section 
        className={`layanan-section-modern ${isVisible['services'] ? 'visible' : ''}`}
        id="services"
        ref={setRef('services')}
      >
        <div className="container">
          <div className="section-header-modern">
            <span className="section-badge">Layanan</span>
            <h2>Platform Digital Kami</h2>
            <p>Pilih layanan yang Anda butuhkan untuk memulai perjalanan akademik digital</p>
          </div>

          <div className="layanan-grid-modern">
            {layananList.map((layanan, index) => (
              <div 
                key={layanan.id} 
                className={`layanan-card-modern ${activeCard === layanan.id ? 'active' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={() => setActiveCard(layanan.id)}
                onMouseLeave={() => setActiveCard(null)}
              >
                {/* Card Glow Effect */}
                <div 
                  className="card-glow"
                  style={{ background: layanan.gradient }}
                ></div>
                
                {/* Card Content */}
                <div className="card-inner">
                  {/* Icon Section */}
                  <div 
                    className="card-icon-wrapper"
                    style={{ background: layanan.gradient }}
                  >
                    <div className="card-icon">
                      {layanan.customIcon ? (
                        <img src={layanan.customIcon} alt={layanan.title} />
                      ) : (
                        <FontAwesomeIcon icon={layanan.icon} />
                      )}
                    </div>
                    <div className="icon-ring"></div>
                  </div>

                  {/* Text Content */}
                  <div className="card-content">
                    <h3>{layanan.title}</h3>
                    <span className="card-subtitle" style={{ color: layanan.color }}>
                      {layanan.subtitle}
                    </span>
                    <p>{layanan.description}</p>

                    {/* Features Tags */}
                    <div className="card-features">
                      {layanan.features && layanan.features.map((feature, idx) => (
                        <span 
                          key={idx} 
                          className="feature-tag"
                          style={{ borderColor: layanan.color, color: layanan.color }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <a 
                    href={layanan.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="card-btn"
                    style={{ 
                      background: layanan.gradient,
                      boxShadow: `0 10px 30px ${layanan.shadowColor}`
                    }}
                  >
                    <span>Akses Layanan</span>
                    <FontAwesomeIcon icon={faArrowRight} className="btn-arrow" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section 
        className={`layanan-cta-section ${isVisible['cta'] ? 'visible' : ''}`}
        id="cta"
        ref={setRef('cta')}
      >
        <div className="container">
          <div className="cta-wrapper">
            <div className="cta-content">
              <h3>Butuh Bantuan?</h3>
              <p>
                Tim IT kami siap membantu Anda menyelesaikan kendala teknis 
                dan pertanyaan seputar layanan digital STIKES Baramuli
              </p>
            </div>
            
            <div className="cta-contacts">
              <div className="contact-card">
                <div className="contact-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <div className="contact-info">
                  <span className="contact-label">Email</span>
                  <a href="mailto:it@stikesbaramuli.ac.id">it@stikesbaramuli.ac.id</a>
                </div>
              </div>

              <div className="contact-card">
                <div className="contact-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <div className="contact-info">
                  <span className="contact-label">Telepon</span>
                  <a href="tel:085824871455">0858-2487-1455</a>
                </div>
              </div>

              <div className="contact-card">
                <div className="contact-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="contact-info">
                  <span className="contact-label">Jam Operasional</span>
                  <span>Senin - Jumat, 08.00 - 16.00 WITA</span>
                </div>
              </div>
            </div>

            {/* Quick Access SIBALI */}
            <a 
              href="https://sibali.stikesbaramuli.ac.id" 
              target="_blank" 
              rel="noopener noreferrer"
              className="sibali-quick-btn"
            >
              <img src="/images/SIBALI.png" alt="SIBALI" />
              <span>Akses SIBALI untuk bantuan cepat</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Layanan;
